import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Convert Arabic numerals (٠١٢٣٤٥٦٧٨٩) to English numerals (0123456789)
export function toEnglishNumbers(str: string | number): string {
  const arabicIndic = "٠١٢٣٤٥٦٧٨٩";
  const easternArabicIndic = "۰۱۲۳۴۵۶۷۸۹";
  return String(str)
    .replace(/[٠-٩]/g, (d) => arabicIndic.indexOf(d).toString())
    .replace(/[۰-۹]/g, (d) => easternArabicIndic.indexOf(d).toString());
}

// Format number with commas using English numerals
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export type Currency = "IQD" | "USD";

export function formatCurrency(amount: number, currency: Currency = "IQD") {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const formattedAmount = formatNumber(safeAmount);
  
  if (currency === "USD") {
    return `$${formattedAmount}`;
  }
  
  // Iraqi Dinar (default)
  return `${formattedAmount} د.ع`;
}

export function getCurrencySymbol(currency: Currency = "IQD") {
  if (currency === "USD") {
    return "$";
  }
  return "د.ع";
}

export function formatDate(date: Date | string | null, locale = 'ar-IQ') {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Normalize Arabic text for fuzzy searching:
 * - Strips diacritics (harakat) and tatweel
 * - أ / إ / آ / ٱ → ا
 * - ة → ه
 * - ى → ي   ئ → ي
 * - ؤ → و
 */
export function normalizeArabic(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\u064B-\u065F\u0670]/g, "") // strip harakat + tatweel
    .replace(/ـ/g, "")                       // remove kashida
    .replace(/[أإآٱ]/g, "ا")                 // normalize alef
    .replace(/ة/g, "ه")                       // ta marbuta → ha
    .replace(/ى/g, "ي")                       // alef maqsura → ya
    .replace(/ئ/g, "ي")                       // hamza on ya → ya
    .replace(/ؤ/g, "و");                      // hamza on waw → waw
}

export function formatRelativeDate(date: Date | string): string {
  const now = new Date();
  const d = new Date(date);
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "الآن";
  if (minutes < 60) return `منذ ${minutes} دقيقة`;
  if (hours < 24) return `منذ ${hours} ساعة`;
  if (days === 1) return "أمس";
  if (days < 7) return `منذ ${days} أيام`;
  return formatDate(date);
}
